import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ShopCardsBlockConfig } from '@front-platform/content-builder';
import {
  normalizeShopCardsMaxColumns,
  shopCardsBlockResolveConfig,
} from '@front-platform/content-builder';
import { Component, DestroyRef, inject, input, OnInit, computed } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { tap } from 'rxjs/operators';
import { EntitiesService } from '@core/services/entities/entities.service';
import { MarketplaceService } from '@core/services/marketplace/marketplace.service';
import { TemplateLayoutKeys } from '@config/keys/template-layout.keys';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
export interface ShopCardsBlockRuntimeConfig {
  'shops'?: any;
  '"bottom-margin"'?: string;
  'display_entity_addresses'?: boolean;
  'entity_name_color'?: IContentBuilderFieldColor;
}

export type ShopCardsBlockConfigInput = Partial<
  Omit<ShopCardsBlockConfig, keyof ShopCardsBlockRuntimeConfig> & ShopCardsBlockRuntimeConfig
>;
export type ShopCardsBlockResolvedConfig = Omit<
  ShopCardsBlockConfig,
  keyof ShopCardsBlockRuntimeConfig
> &
  Partial<ShopCardsBlockRuntimeConfig>;
export interface ShopCardsBlockContract extends ConfigComponentContract<
  ShopCardsBlockConfigInput,
  ShopCardsBlockResolvedConfig
> {}

@Component({
  selector: 'app-shop-cards-block',
  templateUrl: './shop-cards-block.component.html',
  styleUrls: ['./shop-cards-block.component.scss'],
  standalone: false,
})
export class ShopCardsBlockComponent implements OnInit, ShopCardsBlockContract {
  readonly config = input<ShopCardsBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ShopCardsBlockConfig,
    ShopCardsBlockRuntimeConfig
  >(shopCardsBlockResolveConfig, () => ({ config: this.config() }));
  readonly shops = computed(() => this.resolvedConfig()['shops'] as unknown as any);
  readonly title = computed(() => this.resolvedConfig()['title'] as unknown as string);
  readonly titleSize = computed(
    () => this.resolvedConfig()['title_size'] as unknown as 'XS' | 'S' | 'M' | 'L',
  );
  readonly layout = computed(() => this.resolvedConfig()['layout'] as unknown as string);
  readonly maxColumns = computed(() =>
    normalizeShopCardsMaxColumns(this.resolvedConfig()['max_columns']),
  );
  readonly bottomMargin = computed(
    () => this.resolvedConfig()['"bottom-margin"'] as unknown as string,
  );
  readonly kioskLayout = computed(() => this.resolvedConfig()['kioskLayout'] as unknown as string);
  readonly kioskCardAspectRatio = computed(
    () => this.resolvedConfig()['kioskCardAspectRatio'] as unknown as string,
  );
  readonly templateLayout = computed(
    () => this.resolvedConfig()['template_layout'] as unknown as TemplateLayoutKeys,
  );
  readonly backgroundColor = computed(
    () => this.resolvedConfig()['background_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly headingColor = computed(
    () => this.resolvedConfig()['heading_color'] as unknown as IContentBuilderFieldColor,
  );
  readonly entityImageType = computed(
    () => this.resolvedConfig()['entity_image_type'] as unknown as 'cover' | 'overview',
  );
  readonly displayEntityAddresses = computed(
    () => this.resolvedConfig()['display_entity_addresses'] as unknown as boolean,
  );
  readonly entityNameColor = computed(
    () => this.resolvedConfig()['entity_name_color'] as unknown as IContentBuilderFieldColor,
  );

  protected isLoading = true;

  readonly templateLayoutKeys = TemplateLayoutKeys;

  private readonly entitiesService = inject(EntitiesService);
  private readonly marketplaceService = inject(MarketplaceService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.entitiesService
      .loadAll$(this.marketplaceService.getId())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap(() => (this.isLoading = false)),
      )
      .subscribe();
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ShopCardsBlockComponent, {
  'keyAliases': {
    'bottom_margin': '"bottom-margin"',
    'kiosk_layout': 'kioskLayout',
    'kiosk_card_aspect_ratio': 'kioskCardAspectRatio',
  },
});
